import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlContainer } from '@angular/forms';
import { Cancelable, debounce, map, stubFalse } from 'lodash';
import {
  Alignment,
  FormProps,
  InputType,
  InputVariant,
  Option,
} from '../../types/form.type';
@Component({ template: '' })
export class FormComponent implements OnInit {
  menuOptionEl: Element;

  inputControl: AbstractControl; // Input Control

  /* Default Settings Props */
  fontSize = '16px';
  float = false; // for floatable label
  floatInsideLabel = false; // for floatable label inside
  showClear = false;
  placeholder: string = '';
  width: string = '100%';
  variant: InputVariant = 'outlined';
  alignment: Alignment = 'vertical';
  allowNegativeValues = true; // To allow negative values in number input
  allowMultipleSpacing = true; // To allow multiple spaces in text input
  enableTooltip: boolean = false;
  showTooltip: boolean = false;
  tooltipPosition: string = 'top';
  errorMessages: Record<string, string> = {
    // error messages with appropriate error-key
    required: 'This is a required field.',
    whitespace: 'Whitespace is not allowed.',
    min: 'Value should be more than 0',
  };
  tabIndex = ''; // Removes tab focus in input
  type: InputType = 'text';
  dropdownIcon = 'pi pi-chevron-down'; // Arrow icon for dropdown inputs
  isAsync = false; // To register load-more/search option query
  additionalInfo: string = ''; // Info icon text
  subtitle = ''; // subtitle text
  prefix = ''; // subtitle text
  appendToBody = false;
  isAppendToBody: any = false;
  createPrompt: string; // To add cta in dropdown
  inputPrompt: string; // To add input-save after add cta
  _isAutoFocusFilter: boolean = true; // To focus on search input
  searchOffset = 0;
  public get isAutoFocusFilter(): boolean {
    return this._isAutoFocusFilter;
  }
  public set isAutoFocusFilter(value: boolean) {
    this._isAutoFocusFilter = value;
  }

  @Input() isErrorVisible: boolean = true;
  @Input() style: Record<string, string> = {};

  @Output() onCreate = new EventEmitter(); // createPrompt on click emitter
  @Output() onFocus = new EventEmitter(); //handle focus
  @Output() onBlur = new EventEmitter(); //handle focus
  @Output() onKeyDown = new EventEmitter<Event>();

  /* Main Props */
  menuOptions: Option[] = [];
  isDisabled = false;
  isLoading = false;
  showLabel = true;

  /*** Class Input to for attach api event */
  menuClass: string;
  searchInputClass: string;

  /** Internal setting values */
  searchText: string;

  /** To handle api pagination & search call */
  @Input() stopEmission = false;
  @Output() paginate = new EventEmitter<string | void>();
  @Output() onSearch = new EventEmitter<string>();
  @Input() label: string;
  @Input() controlName: string;
  @Input() controlIndex: number;

  @Input() isDisabledLocalFilter: boolean = false; // To disable local filter

  /**
   * To set menu options options
   */
  @Input() set options(input: Option[]) {
    const menu = this.menuNode as HTMLElement | null;
    const nextLength = input?.length || 0;
    const prevLength = this.menuOptions?.length || 0;
    // Preserve scroll only when options are appended (lazy-load), not on search reset.
    const shouldPreserveScroll =
      !!menu && prevLength > 0 && nextLength > prevLength;
    const scrollTop = shouldPreserveScroll ? menu.scrollTop : null;

    if ((!this.menuOptions || !this.menuOptions.length) && !!input?.length) {
      // Registration required as menu class changed when there is no menu Item
      setTimeout(() => {
        this.onMenuOpen();
      }, 500);
    }
    this.menuOptions = input?.length
      ? input?.filter((item) => item?.label?.length && item?.value)
      : [];

    if (scrollTop != null) {
      this.restoreMenuScroll(scrollTop);
    }
  }

  /**
   * PrimeNG rebuilds the options list when the array reference changes, which
   * resets scrollTop to 0. Restore the previous position after the DOM settles.
   */
  private restoreMenuScroll(scrollTop: number) {
    const apply = () => {
      const el = this.menuNode as HTMLElement | null;
      if (!el) return;
      this.suppressScrollPaginate = true;
      el.scrollTop = scrollTop;
      setTimeout(() => {
        this.suppressScrollPaginate = false;
      }, 50);
    };
    apply();
    requestAnimationFrame(() => requestAnimationFrame(apply));
    setTimeout(apply);
  }

  /**
   * To disable input
   */
  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
    this._isAutoFocusFilter = null;
  }

  /**
   * To show loading
   */
  @Input() set loading(value: boolean) {
    if (value) {
      this.dropdownIcon = 'pi pi-spin pi-spinner';
    } else {
      this.dropdownIcon = 'pi pi-chevron-down';
    }
    this.isLoading = value;
  }

  /**
   * To update the value of default [setting] props
   */
  @Input() set props(values: FormProps) {
    if (!values) return;

    for (const key in values) {
      if (values.hasOwnProperty(key)) {
        const val = values[key];

        if (key === 'errorMessages') {
          // To update error messages
          this.errorMessages = { ...this.errorMessages, ...val };
        } else if (this[key] !== val) {
          // Set other properties
          this[key] = val;
        }
      }
    }
  }

  constructor(public controlContainer: ControlContainer) {}

  // Do add these function call if overridden
  ngOnInit(): void {
    this.initInputControl();
  }

  /**
   * Init Input Control
   */
  initInputControl(controlName?: string) {
    this.inputControl = this.controlContainer.control.get(
      controlName ? controlName : this.controlName
    );
    this.addRequiredAsterisk();
    this.inputControl?.valueChanges.subscribe(() => this.addRequiredAsterisk());
  }

  handleBlur(event?: Event) {
    this.onBlur.emit(event);
  }

  handleFocus() {
    this.onFocus.emit();
  }

  handleKeyDown(event: Event) {
    this.onKeyDown.emit(event);
  }

  /**
   * To get all the required props [add value in return if new prop is added]
   * @returns all the required prop
   */
  get props(): FormProps {
    return {
      float: this.float,
      variant: this.variant,
      alignment: this.alignment,
      showClear: this.showClear,
      errorMessages: this.errorMessages,
      fontSize: this.fontSize,
      placeholder: this.placeholder,
      isAsync: this.isAsync,
      dropdownIcon: this.dropdownIcon,
      additionalInfo: this.additionalInfo,
      type: this.type,
      subtitle: this.subtitle,
      prefix: this.prefix,
      isAutoFocusFilter: this.isAutoFocusFilter,
      width: this.width,
      allowMultipleSpacing: this.allowMultipleSpacing,
      allowNegativeValues: this.allowNegativeValues,
      appendToBody: this.appendToBody,
      isAppendToBody: this.isAppendToBody || this.appendToBody,
      enableTooltip: this.enableTooltip,
      showTooltip: this.showTooltip,
      tooltipPosition: this.tooltipPosition,
    };
  }

  /**
   * Input wrapper classes
   */
  get wrapperNgClasses() {
    return {
      // 'p-input-icon-right': this.isLoading,
      wrapper: true,
      'p-float-label': this.float,
      wrapper__vertical: this.alignment === 'vertical',
      wrapper__horizontal: this.alignment === 'horizontal',
      'custom-disabled': this.isDisabled,
    };
  }

  /**
   * Input classes
   */
  get inputNgClasses() {
    return {
      input__static: this.label && this.alignment === 'vertical',
      input__float: this.label && (this.float || this.floatInsideLabel),
      input__standard: this.variant === 'standard',
      input__outlined: this.variant === 'outlined',
      input__error: this.error,
      input__disabled: this.isDisabled,
    };
  }

  /**
   * Get Icon class
   */
  get iconNgClass() {
    return {
      icon: true,
      icon__static: this.label && !this.float,
      icon__float: this.label && this.float,
    };
  }

  /**
   * Get error generated by form control
   */
  get error() {
    const errors = this.controlContainer.control.get(this.controlName)?.errors;
    if (
      errors &&
      this.controlContainer.control.get(this.controlName)?.touched
    ) {
      const priorityError = Object.keys(errors)[0];
      if (errors['minlength']) {
        const requiredLength = errors['minlength'].requiredLength;
        return `It must be at least ${requiredLength} characters long.`;
      }

      if (errors['maxlength']) {
        const requiredLength = errors['maxlength'].requiredLength;
        return `It cannot exceed ${requiredLength} characters.`;
      }
      return this.errorMessages[priorityError];
    }
    return false;
  }

  /**
   * @function addRequiredAsterisk to add [*] in required field
   */
  addRequiredAsterisk() {
    const validators = this.inputControl?.validator;
    const isRequired =
      validators && validators({} as AbstractControl)?.required;
    if (this.label && isRequired && !this.label.includes('*'))
      this.label = this.label + ' *';
    if (this.label && this.label.includes('*') && !isRequired)
      this.label = this.label.replace('*', '');
  }

  /**
   * @function addCreateNewCta to add a cta if createPrompt is required
   */
  addCreateNewCta = () => {
    const id = `${this.controlName}-dropdown-cta`;
    if (!document.getElementById(id) && this.createPrompt) {
      const newDiv = document.createElement('div');
      newDiv.innerText = this.createPrompt;
      newDiv.addEventListener('click', (event: MouseEvent) => {
        if (!this.inputPrompt) {
          this.onCreate.emit();
        } else {
          event.stopPropagation();
          this.addCreateInputCta();
          newDiv.style.display = 'none';
        }
      });
      newDiv.id = id;
      newDiv.className = 'dropdown-action-cta'; // styling class
      const menu = this.menuNode;
      menu?.parentElement.appendChild(newDiv);
    }
  };

  addCreateInputCta = () => {
    const id = `${this.controlName}-dropdown-input-cta`;
    if (!document.getElementById(id) && this.createPrompt) {
      const newDiv = document.createElement('div');
      newDiv.innerHTML = `
      <div style="position: relative; flex-basis: 85%;">
      <input style="width: 100%; height: 100%; padding-right: 54px !important" type="text" id="myInput" pInputText maxlength="60" placeholder="${this.inputPrompt}">
           <small id="subtitle" style="  position: absolute; bottom: 0.8em;right: 0.8em;font-size: 1em; color: lightgray;"></small>

      </div>
      <button id="myButton" onclick="myFunction()">Save</button>

 `;
      const maxLength = 60;
      newDiv.id = id;
      newDiv.className = 'dropdown-input-action-cta'; // styling class
      const menu = this.menuNode;
      menu?.parentElement.appendChild(newDiv);

      const input = document.getElementById('myInput') as HTMLInputElement;
      const button = document.getElementById('myButton');

      const subtitle = document.getElementById('subtitle');
      subtitle.innerHTML = `0/${maxLength}`;

      input.addEventListener('input', () => updateWordCount());

      const updateWordCount = () => {
        const currentTextLength = input.value.length;
        subtitle.innerHTML = `${currentTextLength}/${maxLength}`;
      };

      newDiv.focus();

      input.addEventListener('click', (event: MouseEvent) => {
        event.stopPropagation();
      });
      button.addEventListener('click', () => {
        this.onCreate.emit(input.value);
      });
    }
  };

  get fieldClass() {
    return `${this.controlName}${
      this.controlIndex ? `_${this.controlIndex}` : ''
    }`;
  }

  get menuNode() {
    return document.querySelector(`.${this.fieldClass} .${this.menuClass}`);
  }

  /**
   * @function onMenuOpen To Attach search and pagination api
   */

  scrollListenerFn!: (event: Event) => void;
  searchListenerFn!: (event: Event) => void;
  /** Suppress paginate while we restore scrollTop after appending options */
  private suppressScrollPaginate = false;

  onMenuOpen = (state = '') => {
    if (state) this.dropdownIcon = 'pi pi-chevron-up';
    this.addCreateNewCta();
    if (!this.isAsync) return;

    let debounceCall: (() => void) & Cancelable;

    const menu = this.menuNode;

    const registerScroll = () => {
      if (menu && this.scrollListenerFn) {
        menu.removeEventListener('scroll', this.scrollListenerFn);
      }
      this.scrollListenerFn = () => {
        if (this.stopEmission || this.suppressScrollPaginate) return;
        if (
          !this.isLoading &&
          menu &&
          menu?.scrollHeight - 251 < menu.scrollTop
        ) {
          this.paginate.emit(this.searchText);
        }
      };
      menu?.addEventListener('scroll', this.scrollListenerFn);
    };
    const input = document.querySelector(`.${this.searchInputClass}`);

    const registerSearch = () => {
      if (input && this.searchListenerFn) {
        input.removeEventListener('input', this.searchListenerFn);
      }
      this.searchListenerFn = (event: Event) => {
        this.searchOffset = 0;
        if (this.stopEmission && !this.isDisabledLocalFilter) return;
        this.searchText = event.target['value'];

        // searchRegistered = true;
        debounceCall?.cancel();
        debounceCall = debounce(() => {
          this.onSearch.emit(this.searchText);
          if (menu) menu.scrollTop = 0;
        }, 500);
        debounceCall();
      };
      input?.addEventListener('input', this.searchListenerFn);
    };

    if (!this.stopEmission) {
      registerScroll();
      registerSearch();
    } else if (this.isDisabledLocalFilter) {
      registerSearch();
    }
  };

  /**
   * @function onMenuClose To trigger on close
   */
  onMenuClose(state = '') {
    if (state) this.dropdownIcon = 'pi pi-chevron-down';
    if (this.searchText) {
      // empty search value trigger on menu close
      this.onSearch.emit('');
      this.searchText = '';
    }
    const menu = this.menuNode;
    const input = document.querySelector(`.${this.searchInputClass}`);
    this.cleanupListeners(menu, input);
    this.onBlur.emit();
  }

  cleanupListeners(menu: Element | null, input: Element | null) {
    if (menu && this.scrollListenerFn) {
      menu.removeEventListener('scroll', this.scrollListenerFn);
    }
    if (input && this.searchListenerFn) {
      input.removeEventListener('input', this.searchListenerFn);
    }
  }
}
